import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  Edit,
  Close,
  ExpandMore,
  Delete,
  RestoreFromTrash,
  Visibility,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import SavingOverlay from "../components/SavingOverlay";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  dreConfigurationService,
  DreConfigurationSummary,
  ChartRoot,
} from "../services/dreConfigurationService";
import { colors, typography, pageHeader } from "../theme/designSystem";

// Plano de contas da DRE: o diretor financeiro marca quais categorias/subcategorias
// entram na DRE. Cada salvamento cria uma VERSÃO nova (versões são imutáveis);
// a vigente é a versão ativa de maior número. A v1 é semeada pelo backend com o
// estado efetivo atual do campo DRE do plano de contas do Bubble.

const NOTES_MAX = 255;

const normalize = (value: string): string =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const formatDateTime = (iso: string): string => {
  if (!iso) return "";
  const utc = iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`;
  const date = new Date(utc);
  return `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: "40px",
    fontSize: "1rem",
    backgroundColor: "white",
    "& fieldset": { borderColor: "#ced4da", legend: { maxWidth: "100%" } },
    "&:hover fieldset": { borderColor: "#ced4da" },
    "&.Mui-focused fieldset": {
      borderColor: colors.primary,
      boxShadow: "0 0 0 3px rgba(3, 180, 198, 0.1)",
    },
  },
  "& .MuiOutlinedInput-input, & .MuiSelect-select": {
    padding: "0.375rem 0.5rem",
    color: colors.textPrimary,
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.95rem",
    color: colors.textSecondary,
    backgroundColor: "white",
    paddingLeft: "4px",
    paddingRight: "4px",
    "&.Mui-focused": { color: colors.primary },
  },
};

const clearFilterButtonSx = {
  backgroundColor: "#6c757d",
  color: "white",
  width: "40px",
  height: "40px",
  borderRadius: "4px",
  transition: "all 0.2s ease",
  "&:hover": { backgroundColor: "#5a6268" },
  "&:disabled": { backgroundColor: "#e9ecef", color: "#adb5bd", opacity: 0.6 },
};

// alvo de toque de 44px no celular; 32px no desktop (padrão das listas do sistema)
const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  color: "white",
  width: { xs: 44, sm: 32 },
  height: { xs: 44, sm: 32 },
  "&:hover": { backgroundColor: hover },
  "&.Mui-disabled": { backgroundColor: "#e9ecef", color: "#adb5bd" },
});

const checkboxSx = {
  padding: "4px 8px 4px 4px",
  "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
};

type EditorMode = "create" | "edit" | "view";

const DreChartOfAccounts: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  // escopo da entidade da sessão (nunca de parâmetro da URL)
  const entityId = user?.entityId || "";
  const entityName = user?.entityName || "Entidade";

  const [versions, setVersions] = useState<DreConfigurationSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // filtros reativos da tela
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<"todas" | "ativas" | "inativas">("todas");

  // editor (inclusão/edição geram versão nova; visualização é somente leitura)
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [editorMode, setEditorMode] = useState<EditorMode>("create");
  const [editorLoading, setEditorLoading] = useState<boolean>(false);
  const [baseVersion, setBaseVersion] = useState<DreConfigurationSummary | null>(null);
  const [chart, setChart] = useState<ChartRoot[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState<string>("");
  const [openRoots, setOpenRoots] = useState<Record<string, boolean>>({});
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});

  const [toToggle, setToToggle] = useState<DreConfigurationSummary | null>(null);

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      setVersions(await dreConfigurationService.getByEntity(entityId));
    } catch {
      showToast("Erro ao carregar as versões do plano de contas da DRE.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${entityName} / Plano de contas`;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // vigente = versão ATIVA de maior número
  const currentVersionId = useMemo(() => {
    const active = versions.filter((v) => v.active);
    if (active.length === 0) return "";
    return active.reduce((a, b) => (a.version >= b.version ? a : b)).id;
  }, [versions]);

  const filteredVersions = useMemo(() => {
    const term = normalize(search.trim());
    return versions.filter((v) => {
      if (status === "ativas" && !v.active) return false;
      if (status === "inativas" && v.active) return false;
      if (!term) return true;
      const haystack = normalize(`v${v.version} ${v.notes || ""} ${v.createdByName || ""}`);
      return haystack.includes(term);
    });
  }, [versions, search, status]);

  const hasFilterChanges = search.trim() !== "" || status !== "todas";
  const clearFilters = () => {
    setSearch("");
    setStatus("todas");
  };

  const openEditor = async (mode: EditorMode, base: DreConfigurationSummary | null) => {
    setEditorMode(mode);
    setBaseVersion(base);
    setNotes("");
    setSelected(new Set());
    setEditorOpen(true);
    setEditorLoading(true);
    try {
      const tree = await dreConfigurationService.getChartOfAccounts(entityId);
      setChart(tree);
      setOpenRoots(Object.fromEntries(tree.map((r) => [r.name, true])));
      setOpenCategories({});

      // base da seleção: a versão clicada (editar/visualizar) ou a vigente (nova versão)
      const baseId = base?.id || currentVersionId;
      if (baseId) {
        const detail = await dreConfigurationService.getById(baseId);
        setSelected(new Set(detail.items.map((i) => i.accountTypeExternalId)));
        if (mode === "view") setNotes(detail.notes || "");
      }
    } catch {
      showToast("Erro ao carregar o plano de contas.", "error");
      setEditorOpen(false);
    } finally {
      setEditorLoading(false);
    }
  };

  const closeEditor = () => {
    if (saving) return;
    setEditorOpen(false);
  };

  const toggleNode = (externalId: string, value?: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const target = value !== undefined ? value : !next.has(externalId);
      if (target) next.add(externalId);
      else next.delete(externalId);
      return next;
    });
  };

  const setNodes = (externalIds: string[], value: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      externalIds.forEach((id) => (value ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const handleSave = async () => {
    if (saving) return;
    if (selected.size === 0) {
      showToast("Marque pelo menos uma categoria ou subcategoria.", "error");
      return;
    }
    setSaving(true);
    try {
      await dreConfigurationService.create({
        entityId,
        notes: notes.trim() || undefined,
        selectedExternalIds: Array.from(selected),
      });
      showToast("Nova versão do plano de contas da DRE criada.", "success");
      setEditorOpen(false);
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response?.data
          ?.description || "Erro ao salvar a versão.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!toToggle || saving) return;
    setSaving(true);
    try {
      await dreConfigurationService.setActive(toToggle.id, !toToggle.active);
      showToast(
        toToggle.active ? "Versão inativada." : "Versão reativada.",
        "success"
      );
      setToToggle(null);
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response?.data
          ?.description || "Erro ao alterar o status da versão.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const readOnly = editorMode === "view";

  const editorTitle =
    editorMode === "create"
      ? "Nova versão do plano de contas"
      : editorMode === "edit"
        ? `Editar a partir da versão ${baseVersion?.version} (gera versão nova)`
        : `Versão ${baseVersion?.version} — visualização`;

  if (!entityId) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <HeaderInternal
          showCTAButton={false}
          className="login-header"
          isLoggedIn={true}
          userEmail={user?.email || ""}
          userProfile={user?.role || ""}
          clinicName={user?.clinicName || "Clínica"}
          notificationCount={0}
          onRevalidateLogin={() => {}}
          onNotificationClick={() => {}}
          onUserClick={() => {}}
          onLogoClick={() => navigateTo("dashboard")}
        />
        <Box component="main" sx={{ padding: "1rem", marginTop: "85px", flex: 1 }}>
          <Typography sx={{ color: colors.textSecondary }}>
            Sessão sem entidade definida — faça login novamente.
          </Typography>
        </Box>
        <FooterInternal className="login-footer-component" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />
      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            className="c4u-page-header c4u-page-header--in-panel"
          >
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Plano de contas
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Marque o que entra na DRE por categoria e subcategoria. Cada salvamento cria uma
                versão nova; a vigente é a versão ativa de maior número.
              </Typography>
            </Box>
            <Box className="no-print" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />

            </Box>
          </Box>


          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por versão, observação ou autor"
                  placeholder="Buscar"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as "todas" | "ativas" | "inativas")}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                >
                  <MenuItem value="todas">Todas</MenuItem>
                  <MenuItem value="ativas">Ativas</MenuItem>
                  <MenuItem value="inativas">Inativas</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={() => openEditor("create", null)} title="Nova versão" />
              </div>
            </div>

          <ResultCount shown={filteredVersions.length} total={versions.length} />
          <Box className="admin-plans-list-container" sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
            <Box className="admin-plans-table admin-entities-table">
              <Box
                className="c4u-table__header">
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px", textAlign: "left" }}>
                  Versão
                </Box>
                <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 auto", textAlign: "left" }}>
                  Observação
                </Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px", textAlign: "left" }}>
                  Itens
                </Box>
                <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 170px", textAlign: "left" }}>
                  Autor
                </Box>
                <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 140px", textAlign: "left" }}>
                  Criado em
                </Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 130px", textAlign: "left" }}>
                  Status
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 140px", justifyContent: "flex-end" }}
                >
                  Ações
                </Box>
              </Box>
              <Box className="admin-plans-table-body">
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress size={28} sx={{ color: colors.primary }} />
                  </Box>
                ) : filteredVersions.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: "center", color: colors.textSecondary }}>
                    Nenhuma versão encontrada.
                  </Box>
                ) : (
                  filteredVersions.map((v) => (
                    <Box
                      key={v.id}
                      className="c4u-table__row"
                    >
                      <Box className="admin-plans-cell" sx={{ flex: "0 0 90px" }} data-label="Versão">
                        v{v.version}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "1 1 auto" }} data-label="Observação">
                        {v.notes || "—"}
                      </Box>
                      <Box className="admin-plans-cell" sx={{ flex: "0 0 90px" }} data-label="Itens">
                        {v.itemCount}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 170px" }} data-label="Autor">
                        {v.createdByName || "—"}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "0 0 140px" }} data-label="Criado em">
                        {formatDateTime(v.createdAt)}
                      </Box>
                      <Box className="admin-plans-cell" sx={{ flex: "0 0 130px" }} data-label="Status">
                        {/* Os dois selos num só valor: no cartão do celular a célula é rótulo + valor,
                            e um segundo filho caía na coluna do rótulo */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        <Chip
                          label={v.active ? "Ativa" : "Inativa"}
                          size="small"
                          sx={{
                            backgroundColor: v.active ? "#d4edda" : "#e9ecef",
                            color: v.active ? "#155724" : colors.textSecondary,
                            fontWeight: typography.fontWeight.medium,
                          }}
                        />
                        {v.id === currentVersionId && (
                          <Chip
                            label="Vigente"
                            size="small"
                            sx={{
                              backgroundColor: colors.primaryLight,
                              color: colors.primary,
                              fontWeight: typography.fontWeight.semibold,
                            }}
                          />
                        )}
                        </Box>
                      </Box>
                      <Box
                        className="admin-plans-cell admin-plans-actions"
                        sx={{ flex: "0 0 140px", display: "flex", gap: 0.5, justifyContent: "flex-end" }}
                        data-label="Ações"
                      >
                        <Tooltip title="Visualizar">
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditor("view", v);
                            }}
                            sx={rowActionSx("#6c757d", "#5a6268")}
                          >
                            <Visibility sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar (gera versão nova)">
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditor("edit", v);
                            }}
                            sx={rowActionSx("#17a2b8", "#138496")}
                          >
                            <Edit sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                        {v.active ? (
                          <Tooltip title="Inativar">
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={(e) => {
                              e.stopPropagation();
                              setToToggle(v);
                            }}
                              sx={rowActionSx("#dc3545", "#c82333")}
                            >
                              <Delete sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Reativar">
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={(e) => {
                              e.stopPropagation();
                              setToToggle(v);
                            }}
                              sx={rowActionSx("#28a745", "#218838")}
                            >
                              <RestoreFromTrash sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
          </div>
        </Container>
      </Box>
      <FooterInternal className="login-footer-component" />

      <Dialog
        open={editorOpen}
        onClose={closeEditor}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {editorTitle}
          <IconButton onClick={closeEditor} disabled={saving} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
          {editorLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={28} sx={{ color: colors.primary }} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {!readOnly && (
                <TextField
                  label="Observação"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: NOTES_MAX }}
                  placeholder="Motivo da alteração (opcional)"
                />
              )}
              {readOnly && notes && (
                <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                  Observação: {notes}
                </Typography>
              )}

              {chart.map((root) => {
                const rootNodeIds = root.categories.flatMap((c) => [
                  c.externalId,
                  ...c.subCategories.map((s) => s.externalId),
                ]);
                const rootSelectedCount = rootNodeIds.filter((id) => selected.has(id)).length;
                const rootOpen = openRoots[root.name] ?? true;
                return (
                  <Box
                    key={root.name}
                    sx={{ background: "#fff", borderRadius: 2, border: "1px solid #e9ecef", p: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                      onClick={() =>
                        setOpenRoots((prev) => ({ ...prev, [root.name]: !rootOpen }))
                      }
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          sx={checkboxSx}
                          disabled={readOnly}
                          checked={rootNodeIds.length > 0 && rootSelectedCount === rootNodeIds.length}
                          indeterminate={
                            rootSelectedCount > 0 && rootSelectedCount < rootNodeIds.length
                          }
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setNodes(rootNodeIds, e.target.checked)}
                        />
                        <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold }}>
                          {root.name}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 12, color: colors.textSecondary, ml: 1 }}
                        >
                          {rootSelectedCount} de {rootNodeIds.length} marcadas
                        </Typography>
                      </Box>
                      <IconButton size="small" aria-label={rootOpen ? "Recolher" : "Expandir"}>
                        <ExpandMore
                          fontSize="small"
                          sx={{
                            transition: "transform 200ms",
                            transform: rootOpen ? "rotate(180deg)" : "none",
                          }}
                        />
                      </IconButton>
                    </Box>
                    <Collapse in={rootOpen}>
                      <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
                        {root.categories.map((category) => {
                          const childIds = category.subCategories.map((s) => s.externalId);
                          const childSelected = childIds.filter((id) => selected.has(id)).length;
                          const ownSelected = selected.has(category.externalId);
                          const allSelected =
                            ownSelected && (childIds.length === 0 || childSelected === childIds.length);
                          const partiallySelected =
                            !allSelected && (ownSelected || childSelected > 0);
                          const catOpen = openCategories[category.externalId] ?? false;
                          return (
                            <Box
                              key={category.externalId}
                              sx={{ borderTop: "1px solid #f1f3f5", py: 0.25 }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Checkbox
                                  sx={checkboxSx}
                                  disabled={readOnly}
                                  checked={allSelected}
                                  indeterminate={partiallySelected}
                                  onChange={(e) =>
                                    setNodes([category.externalId, ...childIds], e.target.checked)
                                  }
                                />
                                <Typography
                                  sx={{
                                    fontSize: typography.fontSize.sm,
                                    color: colors.textPrimary,
                                    flex: 1,
                                    cursor: childIds.length > 0 ? "pointer" : "default",
                                  }}
                                  onClick={() =>
                                    childIds.length > 0 &&
                                    setOpenCategories((prev) => ({
                                      ...prev,
                                      [category.externalId]: !catOpen,
                                    }))
                                  }
                                >
                                  {category.name}
                                </Typography>
                                {childIds.length > 0 && (
                                  <IconButton
                                    size="small"
                                    aria-label={catOpen ? "Recolher" : "Expandir"}
                                    onClick={() =>
                                      setOpenCategories((prev) => ({
                                        ...prev,
                                        [category.externalId]: !catOpen,
                                      }))
                                    }
                                  >
                                    <ExpandMore
                                      fontSize="small"
                                      sx={{
                                        transition: "transform 200ms",
                                        transform: catOpen ? "rotate(180deg)" : "none",
                                      }}
                                    />
                                  </IconButton>
                                )}
                              </Box>
                              {childIds.length > 0 && (
                                <Collapse in={catOpen}>
                                  <Box sx={{ pl: 4, display: "flex", flexDirection: "column" }}>
                                    {category.subCategories.map((sub) => (
                                      <Box
                                        key={sub.externalId}
                                        sx={{ display: "flex", alignItems: "center" }}
                                      >
                                        <Checkbox
                                          sx={checkboxSx}
                                          disabled={readOnly}
                                          checked={selected.has(sub.externalId)}
                                          onChange={() => toggleNode(sub.externalId)}
                                        />
                                        <Typography
                                          sx={{
                                            fontSize: typography.fontSize.sm,
                                            color: colors.textSecondary,
                                          }}
                                        >
                                          {sub.name}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Collapse>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}

              {!editorLoading && chart.length === 0 && (
                <Typography sx={{ color: colors.textSecondary, textAlign: "center", p: 2 }}>
                  Plano de contas vazio no espelho do Bubble.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: "1rem 1.5rem" }}>
          <Button
            onClick={closeEditor}
            disabled={saving}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            {readOnly ? "Fechar" : "Cancelar"}
          </Button>
          {!readOnly && (
            <Button
              onClick={handleSave}
              disabled={saving || editorLoading}
              variant="contained"
              sx={{ textTransform: "none", backgroundColor: colors.primary }}
            >
              Salvar nova versão
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={toToggle !== null}
        title={toToggle?.active ? "Inativar versão" : "Reativar versão"}
        message={
          toToggle?.active
            ? `Inativar a versão v${toToggle?.version}? Ela deixa de ser considerada; a vigente passa a ser a versão ativa de maior número.`
            : `Reativar a versão v${toToggle?.version}? Se ela for a ativa de maior número, volta a ser a vigente.`
        }
        confirmButtonText={toToggle?.active ? "Inativar" : "Reativar"}
        type={toToggle?.active ? "danger" : "info"}
        onConfirm={handleToggleActive}
        onCancel={() => !saving && setToToggle(null)}
        isProcessing={saving}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default DreChartOfAccounts;
